import { ExternalLink, FlaskConical, RotateCcw } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Label } from '../../../../components/ui/label';
import { useAppStore } from '../../../../store/appStore';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import {
  BETA_FEATURES,
  BETA_FEATURE_IDS,
  resolveBetaFeature,
  type BetaFeatureId,
} from '../../../../core/beta';

/**
 * Settings → Beta Features.
 *
 * The list is generated from `BETA_FEATURES` so a feature can never be gated
 * without appearing here (and vice versa). Rows are split by the registry's
 * `defaultEnabled`: features that are off until asked for come first under
 * "New in this release", the long-standing badged-Beta ones follow.
 */
function BetaFeatureRow({ id }: { id: BetaFeatureId }) {
  const overrides = useAppStore((s) => s.betaFeatures);
  const setBetaFeature = useAppStore((s) => s.setBetaFeature);
  const { track } = useAnalytics();

  const info = BETA_FEATURES[id];
  const checked = resolveBetaFeature(overrides, id);

  return (
    <div className="flex items-start gap-3">
      <Checkbox
        id={`beta-feature-${id}`}
        data-testid={`beta-feature-${id}`}
        checked={checked}
        onCheckedChange={(next) => {
          const enabled = next === true;
          setBetaFeature(id, enabled);
          // Both directions — turning a beta feature back off is the signal
          // that matters most here.
          track('feature:beta_feature_toggle', { feature: id, enabled });
        }}
      />
      <div className="grid gap-1">
        <Label htmlFor={`beta-feature-${id}`}>{info.name}</Label>
        <div className="text-xs text-muted-foreground">
          {info.description}
          {info.docsUrl && (
            <>
              {' '}
              <a
                href={info.docsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-0.5 underline underline-offset-2 hover:text-foreground"
              >
                Docs
                <ExternalLink className="h-3 w-3" />
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function BetaFeaturesSettingsTab() {
  const overrides = useAppStore((s) => s.betaFeatures);
  const resetBetaFeatures = useAppStore((s) => s.resetBetaFeatures);
  const { track } = useAnalytics();

  const newFeatures = BETA_FEATURE_IDS.filter((id) => !BETA_FEATURES[id].defaultEnabled);
  const establishedFeatures = BETA_FEATURE_IDS.filter((id) => BETA_FEATURES[id].defaultEnabled);
  const hasChoices = Object.keys(overrides).length > 0;

  return (
    <div className="space-y-4" data-testid="beta-features-settings-tab">
      <div className="flex items-start gap-3 rounded-md border border-border bg-muted/30 p-4">
        <FlaskConical className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
        <div className="text-xs text-muted-foreground">
          Beta features work, but they are still changing — expect rough edges
          and the occasional behaviour change between releases. Turning one off
          removes its toolbar button, menu items, shortcuts, and Object Explorer
          entries; tabs you already have open keep working.
        </div>
      </div>

      <div className="space-y-3">
        <div className="text-sm font-medium">New in this release</div>
        <div className="text-xs text-muted-foreground">
          Off until you turn them on.
        </div>
        <div className="space-y-4 rounded-md border border-border bg-background p-4">
          {newFeatures.map((id) => (
            <BetaFeatureRow key={id} id={id} />
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">Established beta features</div>
          {hasChoices && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 px-2 text-xs"
              data-testid="beta-features-reset"
              onClick={() => {
                resetBetaFeatures();
                track('feature:beta_features_reset');
              }}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset to defaults
            </Button>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          On unless you turn them off.
        </div>
        <div className="space-y-4 rounded-md border border-border bg-background p-4">
          {establishedFeatures.map((id) => (
            <BetaFeatureRow key={id} id={id} />
          ))}
        </div>
      </div>
    </div>
  );
}
